import { Info } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/card";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { AudioPicker } from "@/features/audio-picker";
import { ApiError } from "@/lib/api";
import {
  REP_NUMBER_FALLBACKS, REP_NUMBER_WAIT_BOUNDS, formatSpan, repNumberChanges, repNumberFallbackText,
  repNumberProblem, repNumberValues, repNumbers, type RepNumberValues,
} from "@/lib/campaign";
import { useDids, useUpdateCampaign, type CampaignSettingsInput } from "@/lib/queries";
import type { Campaign } from "@/lib/types";

/**
 * What happens to a caller who dials a rep's own number on this campaign when that rep cannot take
 * the call (ADR-037). One rule for every such number: a number only names its rep.
 *
 * Anyone on the line may take the caller after the wait, keeping their place; or nobody else may,
 * and after the wait the call ends, on the closing message where the campaign has one. That
 * message is optional. A rep on a break, signed out or offline is not waited for. The wait is
 * capped by the line's own "Caller waits up to", which the form checks against the primary line
 * before core-api does.
 */
export function CampaignRepNumbers({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const update = useUpdateCampaign();
  const { data: dids } = useDids({ campaign_id: campaign.id });
  const [values, setValues] = React.useState<RepNumberValues>(() => repNumberValues(campaign));
  const [formError, setFormError] = React.useState<string | null>(null);
  const saved = repNumberValues(campaign);
  const changes = repNumberChanges(values, campaign);
  const dirty = Object.keys(changes).length > 0;
  const line = campaign.routing;
  const problem = repNumberProblem(values, line);
  const numbers = [...repNumbers(dids ?? [], campaign.id).values()].flat().length;

  // A refetch after a save, or on focus, re-seeds the form unless somebody is mid-edit.
  React.useEffect(() => {
    if (!dirty) setValues(repNumberValues(campaign));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  const set = <K extends keyof RepNumberValues>(key: K, value: RepNumberValues[K]) =>
    setValues((now) => ({ ...now, [key]: value }));

  const save = async () => {
    setFormError(null);
    try {
      const next = await update.mutateAsync({
        id: campaign.id, ...(changes as CampaignSettingsInput),
      });
      setValues(repNumberValues(next));
      toast.success("Rep numbers saved", campaign.name);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the rule for rep numbers.");
    }
  };

  return (
    <Card>
      <CardHeader
        title="Calls to a rep's own number"
        description="A number can ring one rep on this campaign. This is what happens when that rep can't take the call. It applies to every rep's number here."
        action={
          <Badge tone="outline">
            {numbers === 1 ? "1 rep number" : `${numbers} rep numbers`}
          </Badge>
        }
      />
      <CardBody className="space-y-4">
        {formError && <Callout tone="danger">{formError}</Callout>}
        <fieldset disabled={!canManage} className="min-w-0 space-y-2">
          <legend className="sr-only">When the rep can't take the call</legend>
          <div className="grid gap-2 md:grid-cols-2">
            {REP_NUMBER_FALLBACKS.map((fallback) => {
              const text = repNumberFallbackText(fallback);
              const on = values.rep_number_fallback === fallback;
              return (
                <label
                  key={fallback}
                  className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2 has-[:disabled]:cursor-default"
                >
                  <input
                    type="radio"
                    name="rep-number-fallback"
                    className="mt-0.5 accent-accent-9"
                    checked={on}
                    onChange={() => set("rep_number_fallback", fallback)}
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-text">{text.label}</span>
                    <span className="block text-xs text-text-muted">{text.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={
              values.rep_number_fallback === "rep_only"
                ? "Wait for the rep up to"
                : "Wait for a busy rep up to"
            }
            htmlFor="rep-number-wait"
            hint={
              line?.max_wait_seconds
                ? `Seconds. The line lets a caller wait at most ${formatSpan(line.max_wait_seconds)}.`
                : `Seconds, between ${REP_NUMBER_WAIT_BOUNDS[0]} and ${REP_NUMBER_WAIT_BOUNDS[1]}.`
            }
          >
            <Input
              type="number"
              inputMode="numeric"
              min={REP_NUMBER_WAIT_BOUNDS[0]}
              max={REP_NUMBER_WAIT_BOUNDS[1]}
              className="w-28"
              disabled={!canManage}
              value={Number.isFinite(values.rep_number_wait_seconds) ? values.rep_number_wait_seconds : ""}
              onChange={(e) => set("rep_number_wait_seconds", e.target.valueAsNumber)}
            />
          </Field>
          <Field
            label="Closing message"
            htmlFor="rep-number-message"
            hint={
              values.rep_number_fallback === "rep_only"
                ? "Optional. What the caller hears before the call ends."
                : "Optional. Plays only with Only that rep."
            }
          >
            <AudioPicker
              value={values.rep_number_message}
              onChange={(v) => set("rep_number_message", v)}
              emptyLabel="No message"
              disabled={!canManage}
            />
          </Field>
        </div>

        <ul className="space-y-1.5">
          <li className="flex items-start gap-2 text-xs text-text-muted">
            <Info className="mt-px size-3.5 shrink-0 text-text-subtle" aria-hidden />
            A rep on a break, signed out or with their phone offline can't take calls, so the rule
            applies at once.
          </li>
          <li className="flex items-start gap-2 text-xs text-text-muted">
            <Info className="mt-px size-3.5 shrink-0 text-text-subtle" aria-hidden />
            A number is pointed at a rep on Numbers, or from the Staffing tab.
          </li>
        </ul>

        {dirty && problem && <Callout tone="warning">{problem}</Callout>}
      </CardBody>
      {canManage && (
        <CardFooter>
          <Button
            variant="outline"
            disabled={!dirty || update.isPending}
            onClick={() => {
              setValues(saved);
              setFormError(null);
            }}
          >
            Discard
          </Button>
          <Button
            variant="default"
            loading={update.isPending}
            disabled={!dirty || problem !== null}
            onClick={() => void save()}
          >
            Save
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
